/**
 * HeroTab - Hero section editing with rich subtitle and stat visibility
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Bold, Underline, RemoveFormatting, Eye, EyeOff, Minus, Plus, Smartphone, Tablet, Monitor, Star, Upload, Trash2, CheckCircle, Shield, Zap, Gift, BadgeCheck, Sparkles, Lock, Heart, ThumbsUp, Play } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon options for the primary-CTA subtext (kept in sync with HeroSection.js)
const CTA_SUBTEXT_ICONS = [
  { value: '', label: 'None', Icon: null },
  { value: 'CheckCircle', label: 'Check Circle', Icon: CheckCircle },
  { value: 'BadgeCheck', label: 'Verified Badge', Icon: BadgeCheck },
  { value: 'Shield', label: 'Shield', Icon: Shield },
  { value: 'Lock', label: 'Lock', Icon: Lock },
  { value: 'Star', label: 'Star', Icon: Star },
  { value: 'Sparkles', label: 'Sparkles', Icon: Sparkles },
  { value: 'Zap', label: 'Lightning', Icon: Zap },
  { value: 'Gift', label: 'Gift', Icon: Gift },
  { value: 'Heart', label: 'Heart', Icon: Heart },
  { value: 'ThumbsUp', label: 'Thumbs Up', Icon: ThumbsUp },
];

// Secondary CTA button icon options (legacy default = Play).
const SECONDARY_CTA_ICONS = [
  { value: '', label: 'Play (default)', Icon: Play },
  { value: 'none', label: 'No icon', Icon: null },
  { value: 'CheckCircle', label: 'Check Circle', Icon: CheckCircle },
  { value: 'BadgeCheck', label: 'Verified Badge', Icon: BadgeCheck },
  { value: 'Shield', label: 'Shield', Icon: Shield },
  { value: 'Lock', label: 'Lock', Icon: Lock },
  { value: 'Star', label: 'Star', Icon: Star },
  { value: 'Sparkles', label: 'Sparkles', Icon: Sparkles },
  { value: 'Zap', label: 'Lightning', Icon: Zap },
  { value: 'Gift', label: 'Gift', Icon: Gift },
  { value: 'Heart', label: 'Heart', Icon: Heart },
  { value: 'ThumbsUp', label: 'Thumbs Up', Icon: ThumbsUp },
];

export function HeroTab({ content, setContent }) {
  const subtitleRef = useRef(null);
  const initializedRef = useRef(false);
  const [uploadingBadge, setUploadingBadge] = useState(false);

  // Set initial content once only (uncontrolled contentEditable)
  useEffect(() => {
    if (subtitleRef.current && !initializedRef.current && content.hero?.subtitle) {
      subtitleRef.current.innerHTML = content.hero.subtitle;
      initializedRef.current = true;
    }
  }, [content.hero?.subtitle]);

  const updateHero = (field, value) => {
    setContent(prev => ({ ...prev, hero: { ...prev.hero, [field]: value } }));
  };

  const updateTrustStrip = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        trust_strip: { ...(prev.hero?.trust_strip || {}), [field]: value }
      }
    }));
  };

  const updateHeroStat = (index, field, value) => {
    setContent(prev => {
      const newStats = [...(prev.hero?.stats || [])];
      newStats[index] = { ...newStats[index], [field]: value };
      return { ...prev, hero: { ...prev.hero, stats: newStats } };
    });
  };

  // Wrap selected text in the subtitle contentEditable with a tag
  const wrapSelection = useCallback((tag) => {
    const el = subtitleRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

    const range = sel.getRangeAt(0);
    // Check the selection is inside our editable div
    if (!el.contains(range.commonAncestorContainer)) return;

    const selectedText = range.toString();
    if (!selectedText) return;

    // Check if already wrapped - unwrap if so
    const parent = range.commonAncestorContainer.parentElement;
    if (parent && parent.tagName.toLowerCase() === tag && parent !== el) {
      const textNode = document.createTextNode(parent.textContent);
      parent.parentNode.replaceChild(textNode, parent);
    } else {
      const wrapper = document.createElement(tag);
      range.surroundContents(wrapper);
    }

    // Update state from contentEditable HTML
    updateHero('subtitle', el.innerHTML);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const clearFormatting = useCallback(() => {
    const el = subtitleRef.current;
    if (!el) return;
    const sel = window.getSelection();

    if (sel && sel.rangeCount > 0 && !sel.isCollapsed && el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      // Remove formatting from selection only
      const range = sel.getRangeAt(0);
      const fragment = range.extractContents();
      const text = document.createTextNode(fragment.textContent);
      range.insertNode(text);
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNode(text);
      sel.addRange(newRange);
    } else {
      // No selection: strip all formatting from entire content
      el.innerHTML = el.textContent;
    }
    updateHero('subtitle', el.innerHTML);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubtitleInput = (e) => {
    updateHero('subtitle', e.currentTarget.innerHTML);
  };

  const handleSubtitlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hero Section</CardTitle>
        <CardDescription>Edit the main hero section of the landing page</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-center justify-between gap-3" data-testid="hero-sticky-content-row">
          <div>
            <Label className="text-sm font-semibold text-emerald-900">Pin hero section while scrolling</Label>
            <p className="text-xs text-emerald-800/70 mt-0.5">
              When ON, the hero (headline, copy, CTA) stays fixed in place and the rest of the page scrolls up over it. When OFF, the hero scrolls normally with the page. The background video stays in place either way.
            </p>
          </div>
          <Switch
            checked={content.hero?.stickyContent === true}
            onCheckedChange={(v) => updateHero('stickyContent', v)}
            data-testid="hero-sticky-content-toggle"
          />
        </div>

        {/* Animate hero content in on first paint (fade-in + slide-up, staggered
            across badge → title → subtitle → CTAs → trust strip → stats).
            Respects prefers-reduced-motion automatically. */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-center justify-between gap-3" data-testid="hero-animate-in-row">
          <div>
            <Label className="text-sm font-semibold text-emerald-900">Animate hero content in</Label>
            <p className="text-xs text-emerald-800/70 mt-0.5">
              When ON, each hero element (badge, title, subtitle, CTA row, trust strip, stats) fades in and slides up in sequence when the page first loads. When OFF, everything appears instantly. Users with reduced-motion preferences always get the instant version.
            </p>
          </div>
          <Switch
            checked={content.hero?.heroAnimateIn === true}
            onCheckedChange={(v) => updateHero('heroAnimateIn', v)}
            data-testid="hero-animate-in-toggle"
          />
        </div>

        {/* Primary CTA attention animation — fires ONCE, 10 seconds after
            the CTA enters the viewport. Subtle nudge to draw the visitor's
            eye. Three flavours — see /docs/cta-animation-preview.html for
            the interactive preview. */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="hero-cta-attention-row">
          <Label className="text-sm font-semibold text-emerald-900">Primary CTA attention animation</Label>
          <p className="text-xs text-emerald-800/70 mt-0.5 mb-2">
            Fires once, 10 s after the CTA button enters the viewport. Preview all options at{' '}
            <a href="/docs/cta-animation-preview.html" target="_blank" rel="noreferrer" className="underline">
              /docs/cta-animation-preview.html
            </a>.
          </p>
          <select
            value={content.hero?.ctaAttention || 'shimmer'}
            onChange={(e) => updateHero('ctaAttention', e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-emerald-200 rounded-md text-sm bg-white"
            data-testid="hero-cta-attention-select"
          >
            <option value="off">Off — no animation</option>
            <option value="shimmer">1. Shimmer sweep (default — recommended)</option>
            <option value="halo">2. Soft halo pulse</option>
            <option value="breathe">3. Breathing scale</option>
          </select>

          {/* Delay input — how long after the CTA scrolls into view to
              fire the animation. Default 10 s. Hidden when the animation
              is Off to avoid orphan controls. */}
          {(content.hero?.ctaAttention || 'shimmer') !== 'off' && (
            <>
              <div className="mt-3 flex items-center gap-2" data-testid="hero-cta-attention-delay-row">
                <Label className="text-xs text-emerald-900 shrink-0">Delay before firing</Label>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  step={1}
                  value={
                    Number.isFinite(Number(content.hero?.ctaAttentionDelay))
                      ? Number(content.hero.ctaAttentionDelay)
                      : 10
                  }
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    updateHero('ctaAttentionDelay', Number.isFinite(n) ? Math.max(0, Math.min(60, n)) : 10);
                  }}
                  className="w-20 h-8 text-sm"
                  data-testid="hero-cta-attention-delay-input"
                />
                <span className="text-xs text-emerald-800/70">seconds after CTA enters viewport</span>
              </div>

              {/* Repeat interval — 0 = fire once (default). Values 1-4
                  are clamped up to 5 s on the client to avoid overlapping
                  keyframes. */}
              <div className="mt-2 flex items-center gap-2" data-testid="hero-cta-attention-repeat-row">
                <Label className="text-xs text-emerald-900 shrink-0">Repeat every</Label>
                <Input
                  type="number"
                  min={0}
                  max={300}
                  step={1}
                  value={
                    Number.isFinite(Number(content.hero?.ctaAttentionRepeat))
                      ? Number(content.hero.ctaAttentionRepeat)
                      : 0
                  }
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    updateHero('ctaAttentionRepeat', Number.isFinite(n) ? Math.max(0, Math.min(300, n)) : 0);
                  }}
                  className="w-20 h-8 text-sm"
                  data-testid="hero-cta-attention-repeat-input"
                />
                <span className="text-xs text-emerald-800/70">seconds (0 = fire once, min 5)</span>
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Badge Text</Label>
            <Input value={content.hero?.badge || ''} onChange={(e) => updateHero('badge', e.target.value)} placeholder="e.g., TEAM MANAGEMENT MADE SIMPLE" />
          </div>
          <div>
            <Label>Title Highlight (Colored)</Label>
            <Input value={content.hero?.titleHighlight || ''} onChange={(e) => updateHero('titleHighlight', e.target.value)} placeholder="e.g., Football" />
          </div>
        </div>
        {/* Two independent title font-size groups: pre-highlight text and
            the coloured highlight span. Each has Mobile / Tablet / Desktop
            controls. Highlight breakpoints fall back to the matching
            pre-highlight size when unset. */}
        <div>
          <Label>Title (before highlight)</Label>
          <Input value={content.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} placeholder="e.g., More Time for" />

          {/* Pre-highlight sizes */}
          <div className="mt-3 p-3 bg-gray-50 rounded-lg" data-testid="title-pre-font-size-controls">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-700">Font size — Title (before highlight)</span>
              <span className="text-[10px] text-gray-400">Applies only to the {'"before highlight"'} text</span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Smartphone className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] text-gray-500 font-medium">Mobile</span>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => updateHero('titleMobileFontSize', Math.max(1.5, (content.hero?.titleMobileFontSize || 2) - 0.125))}
                  className="h-6 w-6 p-0" data-testid="title-mobile-font-decrease">
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-xs font-mono w-12 text-center text-gray-600">
                  {(content.hero?.titleMobileFontSize || 2).toFixed(3)}rem
                </span>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => updateHero('titleMobileFontSize', Math.min(4, (content.hero?.titleMobileFontSize || 2) + 0.125))}
                  className="h-6 w-6 p-0" data-testid="title-mobile-font-increase">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Tablet className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] text-gray-500 font-medium">Tablet</span>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => updateHero('titleTabletFontSize', Math.max(2, (content.hero?.titleTabletFontSize || 3) - 0.125))}
                  className="h-6 w-6 p-0" data-testid="title-tablet-font-decrease">
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-xs font-mono w-12 text-center text-gray-600">
                  {(content.hero?.titleTabletFontSize || 3).toFixed(3)}rem
                </span>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => updateHero('titleTabletFontSize', Math.min(6, (content.hero?.titleTabletFontSize || 3) + 0.125))}
                  className="h-6 w-6 p-0" data-testid="title-tablet-font-increase">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Monitor className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] text-gray-500 font-medium">Desktop</span>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => updateHero('titleDesktopFontSize', Math.max(2.5, (content.hero?.titleDesktopFontSize || 4.5) - 0.125))}
                  className="h-6 w-6 p-0" data-testid="title-desktop-font-decrease">
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-xs font-mono w-12 text-center text-gray-600">
                  {(content.hero?.titleDesktopFontSize || 4.5).toFixed(3)}rem
                </span>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => updateHero('titleDesktopFontSize', Math.min(7, (content.hero?.titleDesktopFontSize || 4.5) + 0.125))}
                  className="h-6 w-6 p-0" data-testid="title-desktop-font-increase">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>

          {/* Highlight sizes */}
          <div className="mt-3 p-3 bg-amber-50/60 border border-amber-100 rounded-lg" data-testid="title-highlight-font-size-controls">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-amber-800">Font size — Title Highlight (coloured)</span>
              <span className="text-[10px] text-amber-700/70">Applies only to the coloured highlight — leave as {'"match"'} to inherit</span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {[
                { bp: 'Mobile', key: 'titleHighlightMobileFontSize', Icon: Smartphone, min: 0, max: 4, step: 0.125, fallback: content.hero?.titleMobileFontSize || 2 },
                { bp: 'Tablet', key: 'titleHighlightTabletFontSize', Icon: Tablet, min: 0, max: 6, step: 0.125, fallback: content.hero?.titleTabletFontSize || 3 },
                { bp: 'Desktop', key: 'titleHighlightDesktopFontSize', Icon: Monitor, min: 0, max: 7, step: 0.125, fallback: content.hero?.titleDesktopFontSize || 4.5 },
              ].map(({ bp, key, Icon, min, max, step, fallback }) => {
                const current = Number(content.hero?.[key]) || 0;
                return (
                  <div key={key} className="flex items-center gap-2" data-testid={`title-highlight-${bp.toLowerCase()}-controls`}>
                    <Icon className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-[10px] text-amber-700 font-medium">{bp}</span>
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => updateHero(key, Math.max(min, (current || fallback) - step))}
                      className="h-6 w-6 p-0" data-testid={`title-highlight-${bp.toLowerCase()}-font-decrease`}>
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="text-xs font-mono w-14 text-center text-gray-700">
                      {current > 0 ? `${current.toFixed(3)}rem` : 'match'}
                    </span>
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => updateHero(key, Math.min(max, (current || fallback) + step))}
                      className="h-6 w-6 p-0" data-testid={`title-highlight-${bp.toLowerCase()}-font-increase`}>
                      <Plus className="w-3 h-3" />
                    </Button>
                    {current > 0 && (
                      <button type="button"
                        onClick={() => updateHero(key, 0)}
                        className="text-[10px] text-amber-700 underline hover:text-amber-900"
                        data-testid={`title-highlight-${bp.toLowerCase()}-reset`}>
                        reset
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Subtitle with formatting toolbar */}
        <div>
          <Label className="mb-1.5 block">Subtitle</Label>
          <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200" data-testid="subtitle-toolbar">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => wrapSelection('strong')}
                className="h-7 w-7 p-0 hover:bg-gray-200"
                title="Bold (select text first)"
                data-testid="subtitle-bold-btn"
              >
                <Bold className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => wrapSelection('u')}
                className="h-7 w-7 p-0 hover:bg-gray-200"
                title="Underline (select text first)"
                data-testid="subtitle-underline-btn"
              >
                <Underline className="w-3.5 h-3.5" />
              </Button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFormatting}
                className="h-7 w-7 p-0 hover:bg-gray-200"
                title="Remove formatting (select text or click to clear all)"
                data-testid="subtitle-clear-btn"
              >
                <RemoveFormatting className="w-3.5 h-3.5" />
              </Button>
              <span className="text-[10px] text-gray-400 ml-2">Select text, then click B, U or clear</span>
            </div>
            {/* Editable area */}
            <div
              ref={subtitleRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleSubtitleInput}
              onPaste={handleSubtitlePaste}
              className="px-3 py-2 min-h-[80px] text-sm outline-none leading-relaxed [&_strong]:font-bold [&_u]:underline"
              data-testid="subtitle-editor"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <p className="text-xs text-gray-400">Supports &lt;strong&gt; for bold and &lt;u&gt; for underline</p>
            <div className="flex items-center gap-2" data-testid="mobile-font-size-control">
              <Smartphone className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] text-gray-500 font-medium">Mobile</span>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => updateHero('subtitleMobileFontSize', Math.max(0.75, (content.hero?.subtitleMobileFontSize || 1.125) - 0.0625))}
                className="h-6 w-6 p-0"
                data-testid="mobile-font-decrease"
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="text-xs font-mono w-14 text-center text-gray-700" data-testid="mobile-font-value">
                {(content.hero?.subtitleMobileFontSize || 1.125).toFixed(3)}rem
              </span>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => updateHero('subtitleMobileFontSize', Math.min(2, (content.hero?.subtitleMobileFontSize || 1.125) + 0.0625))}
                className="h-6 w-6 p-0"
                data-testid="mobile-font-increase"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2" data-testid="tablet-font-size-control">
              <Tablet className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] text-gray-500 font-medium">Tablet</span>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => updateHero('subtitleTabletFontSize', Math.max(0.75, (content.hero?.subtitleTabletFontSize || 1.25) - 0.0625))}
                className="h-6 w-6 p-0"
                data-testid="tablet-font-decrease"
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="text-xs font-mono w-14 text-center text-gray-700" data-testid="tablet-font-value">
                {(content.hero?.subtitleTabletFontSize || 1.25).toFixed(3)}rem
              </span>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => updateHero('subtitleTabletFontSize', Math.min(3, (content.hero?.subtitleTabletFontSize || 1.25) + 0.0625))}
                className="h-6 w-6 p-0"
                data-testid="tablet-font-increase"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2" data-testid="desktop-font-size-control">
              <Monitor className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] text-gray-500 font-medium">Desktop</span>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => updateHero('subtitleDesktopFontSize', Math.max(0.75, (content.hero?.subtitleDesktopFontSize || 1.25) - 0.0625))}
                className="h-6 w-6 p-0"
                data-testid="desktop-font-decrease"
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="text-xs font-mono w-14 text-center text-gray-700" data-testid="desktop-font-value">
                {(content.hero?.subtitleDesktopFontSize || 1.25).toFixed(3)}rem
              </span>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => updateHero('subtitleDesktopFontSize', Math.min(3, (content.hero?.subtitleDesktopFontSize || 1.25) + 0.0625))}
                className="h-6 w-6 p-0"
                data-testid="desktop-font-increase"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Subtitle Text Colour */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 rounded-lg" data-testid="subtitle-color-section">
            <div>
              <Label className="text-xs font-medium mb-2 block">Subtitle Colour — Light Theme</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={content.hero?.subtitleColorLight || '#4b5563'}
                  onChange={(e) => updateHero('subtitleColorLight', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                  data-testid="subtitle-color-light-picker"
                />
                <Input
                  value={content.hero?.subtitleColorLight || '#4b5563'}
                  onChange={(e) => updateHero('subtitleColorLight', e.target.value)}
                  placeholder="#4b5563"
                  className="w-28 font-mono text-xs h-8"
                  data-testid="subtitle-color-light-hex"
                />
                <div className="flex-1 rounded px-2 py-1 border border-gray-200 bg-white" data-testid="subtitle-color-light-preview">
                  <span className="text-xs" style={{ color: content.hero?.subtitleColorLight || '#4b5563' }}>Preview text</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium mb-2 block">Subtitle Colour — Dark Theme</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={content.hero?.subtitleColorDark || '#9ca3af'}
                  onChange={(e) => updateHero('subtitleColorDark', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                  data-testid="subtitle-color-dark-picker"
                />
                <Input
                  value={content.hero?.subtitleColorDark || '#9ca3af'}
                  onChange={(e) => updateHero('subtitleColorDark', e.target.value)}
                  placeholder="#9ca3af"
                  className="w-28 font-mono text-xs h-8"
                  data-testid="subtitle-color-dark-hex"
                />
                <div className="flex-1 rounded px-2 py-1 border border-gray-200 bg-gray-900" data-testid="subtitle-color-dark-preview">
                  <span className="text-xs" style={{ color: content.hero?.subtitleColorDark || '#9ca3af' }}>Preview text</span>
                </div>
              </div>
            </div>
          </div>

          {/* Subtitle Letter Spacing */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-testid="subtitle-letter-spacing-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-medium">Subtitle Letter Spacing</Label>
              <span className="text-[11px] font-mono text-gray-600" data-testid="subtitle-letter-spacing-value">
                {(content.hero?.subtitleLetterSpacing ?? 0).toFixed(3)}em
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-gray-400 font-mono w-10 text-right">-0.05</span>
              <input
                type="range"
                min="-0.05"
                max="0.15"
                step="0.005"
                value={content.hero?.subtitleLetterSpacing ?? 0}
                onChange={(e) => updateHero('subtitleLetterSpacing', parseFloat(e.target.value))}
                className="flex-1 accent-[#007a33]"
                data-testid="subtitle-letter-spacing-slider"
              />
              <span className="text-[10px] text-gray-400 font-mono w-10">+0.15</span>
              {(content.hero?.subtitleLetterSpacing ?? 0) !== 0 && (
                <button
                  type="button"
                  onClick={() => updateHero('subtitleLetterSpacing', 0)}
                  className="text-xs text-gray-500 underline hover:text-gray-800 whitespace-nowrap"
                  data-testid="subtitle-letter-spacing-reset"
                >
                  Reset
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              Live preview: <span
                className="text-gray-900"
                style={{
                  fontWeight: content.hero?.subtitleFontWeight || 400,
                  letterSpacing: `${content.hero?.subtitleLetterSpacing ?? 0}em`,
                }}
              >
                Manage your team, plan training, run match days hands-free.
              </span>
            </p>
          </div>

          {/* Subtitle Font Weight */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-testid="subtitle-weight-section">
            <Label className="text-xs font-medium mb-2 block">Subtitle Weight</Label>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { v: 300, label: 'Light' },
                { v: 400, label: 'Normal' },
                { v: 500, label: 'Medium' },
                { v: 600, label: 'Semibold' },
                { v: 700, label: 'Bold' },
                { v: 800, label: 'Extra' },
              ].map((opt) => {
                const active = (content.hero?.subtitleFontWeight || 400) === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => updateHero('subtitleFontWeight', opt.v)}
                    data-testid={`subtitle-weight-${opt.v}`}
                    className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                      active
                        ? 'bg-[#007a33] border-[#007a33] text-white'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    style={{ fontWeight: opt.v }}
                  >
                    {opt.label}
                    <span className="ml-1 text-[10px] opacity-70">{opt.v}</span>
                  </button>
                );
              })}
              {content.hero?.subtitleFontWeight && (
                <button
                  type="button"
                  onClick={() => updateHero('subtitleFontWeight', null)}
                  className="ml-2 text-xs text-gray-500 underline hover:text-gray-800"
                  data-testid="subtitle-weight-reset"
                >
                  Reset to default
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              Live preview: <span className="text-gray-900" style={{ fontWeight: content.hero?.subtitleFontWeight || 400 }}>
                Manage your team, plan training, run match days hands-free.
              </span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Primary CTA Text</Label>
            <Input value={content.hero?.ctaText || ''} onChange={(e) => updateHero('ctaText', e.target.value)} placeholder="e.g., Start Free Today" />

            <Label className="mt-3 block text-xs text-gray-500">On click</Label>
            <select
              value={content.hero?.ctaPrimaryAction || 'signup'}
              onChange={(e) => updateHero('ctaPrimaryAction', e.target.value)}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="cta-primary-action"
            >
              <option value="signup">Go direct to signup / onboarding</option>
              <option value="scroll">Scroll to section on this page</option>
            </select>

            {content.hero?.ctaPrimaryAction === 'scroll' && (
              <>
                <Label className="mt-2 block text-xs text-gray-500">Scrolls to</Label>
                <select
                  value={content.hero?.ctaPrimaryTarget || 'pricing'}
                  onChange={(e) => updateHero('ctaPrimaryTarget', e.target.value)}
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="cta-primary-target"
                >
                  <option value="pricing">Pricing</option>
                  <option value="dashboard_preview">Dashboard Preview</option>
                  <option value="features">Features</option>
                  <option value="customization">Make It Yours</option>
                  <option value="video">Product Demo / Video</option>
                  <option value="gallery">Image Gallery</option>
                  <option value="testimonials">Testimonials</option>
                  <option value="faq">FAQ</option>
                  <option value="contact">Contact</option>
                  <option value="final_cta">Final CTA</option>
                </select>

                {(content.hero?.ctaPrimaryTarget || 'pricing') === 'pricing' && (
                  <>
                    <Label className="mt-2 block text-xs text-gray-500">Pricing slider starts at</Label>
                    <select
                      value={content.hero?.ctaPrimaryPrefillWeeks ?? ''}
                      onChange={(e) => updateHero('ctaPrimaryPrefillWeeks', e.target.value === '' ? null : Number(e.target.value))}
                      className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="cta-primary-prefill-weeks"
                    >
                      <option value="">Default (4 weeks — no prefill)</option>
                      <option value="1">1 week — Try it free / Weekly</option>
                      <option value="4">4 weeks — Monthly</option>
                      <option value="12">12 weeks — Quarter season</option>
                      <option value="24">24 weeks — Half season</option>
                      <option value="38">38 weeks — Full season</option>
                    </select>
                  </>
                )}
              </>
            )}

            <Label className="mt-3 block text-xs text-gray-500">Subtext (small line under the primary CTA — leave blank to hide)</Label>
            <Input
              value={content.hero?.ctaSubtext || ''}
              onChange={(e) => updateHero('ctaSubtext', e.target.value)}
              placeholder="e.g., No credit card required"
              data-testid="cta-subtext-input"
              className="mt-1"
            />
            <div className="mt-2 flex items-center gap-2" data-testid="cta-subtext-icon-row">
              <Label className="text-[10px] text-gray-500 font-medium whitespace-nowrap">Icon</Label>
              <select
                value={content.hero?.ctaSubtextIcon || ''}
                onChange={(e) => updateHero('ctaSubtextIcon', e.target.value)}
                disabled={!content.hero?.ctaSubtext}
                className="flex h-8 flex-1 rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="cta-subtext-icon-select"
              >
                {CTA_SUBTEXT_ICONS.map((opt) => (
                  <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {(() => {
                const sel = CTA_SUBTEXT_ICONS.find(o => o.value === (content.hero?.ctaSubtextIcon || ''));
                const Icon = sel?.Icon;
                return Icon ? <Icon className="w-4 h-4 text-[#007a33]" data-testid="cta-subtext-icon-preview" /> : <span className="w-4 h-4" />;
              })()}
            </div>
            <div className="mt-2 flex items-center gap-2" data-testid="cta-subtext-color-row">
              <Label className="text-[10px] text-gray-500 font-medium whitespace-nowrap">Color</Label>
              <input
                type="color"
                value={content.hero?.ctaSubtextColor || '#9ca3af'}
                onChange={(e) => updateHero('ctaSubtextColor', e.target.value)}
                disabled={!content.hero?.ctaSubtext}
                className="h-8 w-10 rounded-md border border-input bg-background p-0.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="cta-subtext-color-picker"
                aria-label="Subtext color"
              />
              <Input
                value={content.hero?.ctaSubtextColor || ''}
                onChange={(e) => updateHero('ctaSubtextColor', e.target.value)}
                disabled={!content.hero?.ctaSubtext}
                placeholder="Theme default (auto)"
                className="h-8 text-xs flex-1"
                data-testid="cta-subtext-color-hex"
              />
              {content.hero?.ctaSubtextColor && (
                <button
                  type="button"
                  onClick={() => updateHero('ctaSubtextColor', '')}
                  className="text-[10px] text-gray-500 hover:text-gray-700 underline whitespace-nowrap"
                  data-testid="cta-subtext-color-reset"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Secondary CTA Text</Label>
              <div className="flex items-center gap-1.5" data-testid="secondary-cta-visibility">
                {content.hero?.secondaryCtaVisible === false ? (
                  <EyeOff className="w-3 h-3 text-gray-400" />
                ) : (
                  <Eye className="w-3 h-3 text-emerald-600" />
                )}
                <Switch
                  checked={content.hero?.secondaryCtaVisible !== false}
                  onCheckedChange={(checked) => updateHero('secondaryCtaVisible', checked)}
                  className="scale-75"
                  data-testid="secondary-cta-toggle"
                />
              </div>
            </div>
            <Input
              value={content.hero?.ctaSecondary || ''}
              onChange={(e) => updateHero('ctaSecondary', e.target.value)}
              placeholder="e.g., See How It Works"
              disabled={content.hero?.secondaryCtaVisible === false}
            />
          </div>
          <div>
            <Label>Secondary CTA Scrolls To</Label>
            <select
              value={content.hero?.ctaSecondaryTarget || 'demo'}
              onChange={(e) => updateHero('ctaSecondaryTarget', e.target.value)}
              disabled={content.hero?.secondaryCtaVisible === false}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cta-secondary-target"
            >
              <option value="dashboard_preview">Dashboard Preview</option>
              <option value="features">Features</option>
              <option value="customization">Make It Yours</option>
              <option value="video">Product Demo / Video</option>
              <option value="voice_features">Voice Features</option>
              <option value="gallery">Image Gallery</option>
              <option value="testimonials">Testimonials</option>
              <option value="pricing">Pricing</option>
              <option value="faq">FAQ</option>
              <option value="primary_cta">Primary CTA</option>
              <option value="secondary_cta">Secondary CTA</option>
              <option value="contact">Contact</option>
              <option value="final_cta">Final CTA</option>
            </select>

            {(content.hero?.ctaSecondaryTarget || 'demo') === 'pricing' && (
              <>
                <Label className="mt-2 block text-xs text-gray-500">Pricing slider starts at</Label>
                <select
                  value={content.hero?.ctaSecondaryPrefillWeeks ?? ''}
                  onChange={(e) => updateHero('ctaSecondaryPrefillWeeks', e.target.value === '' ? null : Number(e.target.value))}
                  disabled={content.hero?.secondaryCtaVisible === false}
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="cta-secondary-prefill-weeks"
                >
                  <option value="">Default (4 weeks — no prefill)</option>
                  <option value="1">1 week — Try it free / Weekly</option>
                  <option value="4">4 weeks — Monthly</option>
                  <option value="12">12 weeks — Quarter season</option>
                  <option value="24">24 weeks — Half season</option>
                  <option value="38">38 weeks — Full season</option>
                </select>
              </>
            )}
          </div>

          {/* Secondary CTA icon picker */}
          <div className="mt-3">
            <Label>Secondary CTA Icon</Label>
            <select
              value={content.hero?.ctaSecondaryIcon ?? ''}
              onChange={(e) => updateHero('ctaSecondaryIcon', e.target.value)}
              disabled={content.hero?.secondaryCtaVisible === false}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cta-secondary-icon"
            >
              {SECONDARY_CTA_ICONS.map((opt) => (
                <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {(() => {
              const sel = SECONDARY_CTA_ICONS.find(o => o.value === (content.hero?.ctaSecondaryIcon ?? ''));
              return sel?.Icon ? (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 border border-gray-200 text-sm text-gray-700">
                  <sel.Icon className="w-4 h-4 text-gray-700" />
                  <span>Preview</span>
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Stats with visibility toggles */}
        <div>
          <Label className="mb-3 block">Stats</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {content.hero?.stats?.map((stat, index) => (
              <div key={index} className={`bg-gray-50 p-4 rounded-lg space-y-2 transition-opacity ${stat.visible === false ? 'opacity-50' : ''}`} data-testid={`hero-stat-${index}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500">Stat {index + 1}</span>
                  <div className="flex items-center gap-1.5">
                    {stat.visible === false ? (
                      <EyeOff className="w-3 h-3 text-gray-400" />
                    ) : (
                      <Eye className="w-3 h-3 text-emerald-600" />
                    )}
                    <Switch
                      checked={stat.visible !== false}
                      onCheckedChange={(checked) => updateHeroStat(index, 'visible', checked)}
                      className="scale-75"
                      data-testid={`stat-toggle-${index}`}
                    />
                  </div>
                </div>
                <Input value={stat.value} onChange={(e) => updateHeroStat(index, 'value', e.target.value)} placeholder="e.g., 500+" data-testid={`stat-value-${index}`} />
                <Input value={stat.label} onChange={(e) => updateHeroStat(index, 'label', e.target.value)} placeholder="e.g., Active Teams" data-testid={`stat-label-${index}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Community Trust Strip */}
        <div className="border-t pt-6">
          <Label className="mb-3 block text-base font-semibold">Community Trust Strip</Label>
          <p className="text-xs text-gray-500 mb-4">Displayed below the hero CTA buttons. Upload a badge icon, set star rating, and add trust text.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Badge Upload */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3" data-testid="trust-strip-badge-section">
              <span className="text-xs font-medium text-gray-500 block">Badge Icon</span>
              {content.hero?.trust_strip?.badge_url ? (
                <div className="flex items-center gap-3">
                  <img
                    src={content.hero.trust_strip.badge_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${content.hero.trust_strip.badge_url}` : content.hero.trust_strip.badge_url}
                    alt="Trust badge"
                    className="h-10 w-auto object-contain border rounded bg-white p-1"
                    data-testid="trust-strip-badge-preview"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => updateTrustStrip('badge_url', '')}
                    className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                    data-testid="trust-strip-badge-remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : null}
              <label
                className={`flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors ${uploadingBadge ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="trust-strip-badge-upload"
              >
                <Upload className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">{uploadingBadge ? 'Uploading...' : 'Upload badge image'}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingBadge(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const token = localStorage.getItem('token');
                      const res = await axios.post(`${API}/landing/upload-trust-badge`, formData, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      updateTrustStrip('badge_url', res.data.badge_url);
                    } catch (err) {
                      console.error('Badge upload failed:', err);
                    } finally {
                      setUploadingBadge(false);
                      e.target.value = '';
                    }
                  }}
                />
              </label>
            </div>

            {/* Star Count */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3" data-testid="trust-strip-star-section">
              <span className="text-xs font-medium text-gray-500 block">Star Rating (green out of 5)</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => updateTrustStrip('star_count', n)}
                    className="p-0.5 transition-transform hover:scale-110"
                    data-testid={`trust-strip-star-${n}`}
                  >
                    <Star
                      className={`w-7 h-7 ${n <= (content.hero?.trust_strip?.star_count ?? 5) ? 'fill-[#007a33] text-[#007a33]' : 'fill-gray-300 text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400">{content.hero?.trust_strip?.star_count ?? 5} of 5 stars green</p>
            </div>

            {/* Trust Text */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3" data-testid="trust-strip-text-section">
              <span className="text-xs font-medium text-gray-500 block">Trust Text</span>
              <Input
                value={content.hero?.trust_strip?.text || ''}
                onChange={(e) => updateTrustStrip('text', e.target.value)}
                placeholder="e.g., Trusted by 500+ coaches"
                data-testid="trust-strip-text-input"
              />
              {/* Colour overrides — leave blank to inherit the theme's
                  default secondary-text colour (matches the hero subtitle
                  pattern used above). */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                <div>
                  <span className="text-[10px] font-medium text-gray-500 block mb-1">Light theme colour</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={content.hero?.trust_strip?.text_color_light || '#4b5563'}
                      onChange={(e) => updateTrustStrip('text_color_light', e.target.value)}
                      className="w-9 h-8 rounded cursor-pointer border border-gray-200"
                      data-testid="trust-strip-color-light-picker"
                    />
                    <Input
                      value={content.hero?.trust_strip?.text_color_light || ''}
                      onChange={(e) => updateTrustStrip('text_color_light', e.target.value)}
                      placeholder="#4b5563"
                      className="text-xs h-8 flex-1"
                      data-testid="trust-strip-color-light-input"
                    />
                    {content.hero?.trust_strip?.text_color_light && (
                      <button
                        type="button"
                        onClick={() => updateTrustStrip('text_color_light', '')}
                        className="text-[10px] text-gray-500 underline hover:text-gray-800"
                        data-testid="trust-strip-color-light-reset"
                      >
                        reset
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-[10px] font-medium text-gray-500 block mb-1">Dark theme colour</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={content.hero?.trust_strip?.text_color_dark || '#9ca3af'}
                      onChange={(e) => updateTrustStrip('text_color_dark', e.target.value)}
                      className="w-9 h-8 rounded cursor-pointer border border-gray-200"
                      data-testid="trust-strip-color-dark-picker"
                    />
                    <Input
                      value={content.hero?.trust_strip?.text_color_dark || ''}
                      onChange={(e) => updateTrustStrip('text_color_dark', e.target.value)}
                      placeholder="#9ca3af"
                      className="text-xs h-8 flex-1"
                      data-testid="trust-strip-color-dark-input"
                    />
                    {content.hero?.trust_strip?.text_color_dark && (
                      <button
                        type="button"
                        onClick={() => updateTrustStrip('text_color_dark', '')}
                        className="text-[10px] text-gray-500 underline hover:text-gray-800"
                        data-testid="trust-strip-color-dark-reset"
                      >
                        reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 leading-snug">
                Leave blank to inherit the theme&apos;s default. The active colour follows the site&apos;s current theme (light/dark).
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
